import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Quote, Star, ChevronLeft, ChevronRight } from "lucide-react";

export default function TestimonialCarousel({ testimonials = [] }) {
  const [index, setIndex] = useState(0);
  const count = testimonials.length;

  const next = useCallback(
    () => setIndex((i) => (i + 1) % count),
    [count]
  );
  const prev = () => setIndex((i) => (i - 1 + count) % count);

  useEffect(() => {
    if (count <= 1) return;
    const id = setInterval(next, 7000);
    return () => clearInterval(id);
  }, [count, next]);

  if (!count) return null;
  const t = testimonials[index];

  return (
    <div data-testid="testimonial-carousel" className="relative mt-14 max-w-4xl mx-auto">
      <div className="relative rounded-[2rem] ink-gradient grain text-white p-9 md:p-14 overflow-hidden min-h-[340px] flex items-center">
        <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-gold/15 blur-3xl" />
        <AnimatePresence mode="wait">
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full"
            data-testid={`testimonial-slide-${index}`}
          >
            <Quote className="h-9 w-9 text-gold" />
            <div className="mt-5 flex gap-1">
              {[...Array(5)].map((_, k) => (
                <Star key={k} className="h-4 w-4 fill-gold text-gold" />
              ))}
            </div>
            <p className="mt-5 font-display text-xl md:text-[1.6rem] leading-snug italic text-white">
              “{t.quote}”
            </p>
            <div className="mt-8 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-white">{t.name}</div>
                <div className="text-xs uppercase tracking-[0.18em] text-white/60 mt-1">
                  {t.matter}
                </div>
              </div>
              {t.footer && (
                <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-gold text-right">
                  {t.footer}
                </div>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="mt-7 flex items-center justify-center gap-5">
        <button
          type="button"
          onClick={prev}
          aria-label="Previous testimonial"
          data-testid="testimonial-prev"
          className="h-11 w-11 rounded-full border border-navy-900/15 grid place-items-center text-navy-900 hover:bg-gold hover:text-navy-950 hover:border-gold transition-colors"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div className="flex items-center gap-2">
          {testimonials.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`Go to testimonial ${i + 1}`}
              data-testid={`testimonial-dot-${i}`}
              className={
                "h-2 rounded-full transition-all duration-300 " +
                (i === index ? "w-8 bg-gold" : "w-2 bg-navy-900/25")
              }
            />
          ))}
        </div>
        <button
          type="button"
          onClick={next}
          aria-label="Next testimonial"
          data-testid="testimonial-next"
          className="h-11 w-11 rounded-full border border-navy-900/15 grid place-items-center text-navy-900 hover:bg-gold hover:text-navy-950 hover:border-gold transition-colors"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
